import { App, Flex, Grid, Skeleton } from "antd"
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { DEFAULT_TAB } from "../../../components/EntityTable/surfaces.ts"
import { selectionResetToken } from "../../../components/EntityTable/use-row-selection.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useSurfacePreference } from "../../../hooks/use-surface-preference.ts"
import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../store.ts"
import { downloadBlob } from "../../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../utils/utils"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import type { StatusChangeOptions } from "../../Project/components/ProjectStatusSelector.tsx"
import { sendStatusChange } from "../../Project/helpers/send-status-change.ts"
import {
  updateListProject,
  updateProjectStatus
} from "../../Project/redux/project.thunks.ts"
import { selectTableTabColumns } from "../../User/redux/ui-preferences.selectors.ts"
import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"
import { ArchiveProjectModal } from "../components/ArchiveProjectModal.tsx"
import { UnpaidInvoicesDialog } from "../components/UnpaidInvoicesDialog.tsx"
import { canCloseProject } from "../../Project/helpers/project-edit-visibility.ts"
import { DEFAULT_PROJECT_ORDERING } from "../constants/created.ts"
import { withoutArchivingStatuses } from "../constants/project-statuses.ts"
import {
  canSeeProjectBudget,
  isPriceOrdering
} from "../helpers/project-budget-visibility.ts"
import {
  useFilterUrlSync,
  useProjectsUrlState
} from "../helpers/projects-url-state.ts"
import { projectsSetListFilters } from "../redux/projects.slice.ts"
import {
  ProjectFetchType,
  bulkArchiveProjects,
  bulkPatchProjects,
  deleteProject,
  fetchImportProjectsTemplates,
  fetchProjects,
  fetchProjectsFilterOptions,
  importProjects,
  restoreProject
} from "../redux/projects.thunks.ts"
import {
  type ProjectBulkResult,
  type ProjectFilters
} from "../redux/projects.types.ts"
import type { ProjectListItem } from "../types/types.ts"
import { type ProjectEditableRowData } from "./components/ProjectListEditableRow.tsx"
import { ProjectListTable } from "./components/ProjectListTable.tsx"
import { ProjectListTableFilters } from "./components/ProjectListTableFilters.tsx"
import { ProjectListTableHeader } from "./components/ProjectListTableHeader.tsx"
import { type ProjectBulkActionParams } from "./components/ProjectsBulkActionBar.tsx"
import { confirmProjectDelete } from "./delete-confirmation.ts"
import { projectRowActionRoute } from "./project-row-action-route.ts"
import { ProjectCommandToolbar } from "./redesign/ProjectCommandToolbar.tsx"
import { ProjectQuickViews } from "./redesign/ProjectQuickViews.tsx"
import { CLEARED_FILTERS } from "./redesign/active-filter-chips.ts"
import {
  PROJECT_COMMAND_SURFACE,
  scheduleColumnShown
} from "./redesign/redesign-columns.tsx"

// Loaded only when the redesign is on: the flagged header is never part of a
// page that cannot show it.
const ProjectsCommandHeader = lazy(() =>
  import("./redesign/ProjectsCommandHeader.tsx").then((module) => ({
    default: module.ProjectsCommandHeader
  }))
)

// The value the backend's project list endpoint expects for the Archive —
// unrelated to routing now that Active and Archive are separate destinations.
const PROJECT_ARCHIVE_VIEW = "archive"

// The list's own remembered sort — set once a user picks a column and read
// back on every later visit, matching the Leads list's own behavior. Active
// and Archive get separate keys (like `PROJECT_ARCHIVE_SURFACE` for column
// visibility below) since a saved sort on one can name a column the other
// doesn't have. Last Activity is the Active list's fallback for anyone who
// has never picked one; Archive keeps its existing Date Created default,
// since nobody asked to change that one.
const PROJECTS_SORT_PREFERENCE_KEY = surfaceKey("projects.sort")
const PROJECTS_ARCHIVE_SORT_PREFERENCE_KEY = surfaceKey("projects.archive.sort")
const DEFAULT_PROJECTS_LIST_ORDERING = "-updated"

interface ProjectsListPageOwnProps {
  archived: boolean
  /** Mounted inside the Archive page, which supplies its own header and
   *  document title — so this one renders neither and would otherwise stack
   *  a second page header under the Archive's. */
  embedded?: boolean
}

export function ProjectsListPageBase({
  archived,
  embedded = false,
  data,
  filters,
  fetching,
  filterOptions,
  projectPermissions,
  quickViewCounts,
  redesignEnabled,
  scheduleShown = true,
  updateFilters,
  updateProject,
  updateProjectStatus,
  fetchProjects,
  fetchFilterOptions,
  getImportProjectsTemplates,
  importProjects,
  restoreProject,
  deleteProject,
  bulkPatchProjects,
  bulkArchiveProjects
}: ConnectedProps<typeof connector> & ProjectsListPageOwnProps) {
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const [ordering, setOrdering] = useSurfacePreference<string | null>(
    archived
      ? PROJECTS_ARCHIVE_SORT_PREFERENCE_KEY
      : PROJECTS_SORT_PREFERENCE_KEY,
    archived ? DEFAULT_PROJECT_ORDERING : DEFAULT_PROJECTS_LIST_ORDERING
  )
  // A price sort saved before the caller lost sight of prices is dropped once
  // their permissions are known: it would sort by a column they cannot see.
  const priceVisible = canSeeProjectBudget(projectPermissions)
  useEffect(() => {
    if (projectPermissions && !priceVisible && isPriceOrdering(ordering))
      setOrdering(null)
  }, [projectPermissions, priceVisible, ordering, setOrdering])
  const [projectToArchive, setProjectToArchive] =
    useState<ProjectListItem | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false)
  // The redesigned command center replaces the active list only; the Archive
  // keeps its own page, columns and filters.
  const redesign = !!redesignEnabled && !archived && !embedded
  const closedAllowed = !!projectPermissions?.features?.project_archive
  const { quickView, scope, setQuickView, setScope, resetViews } =
    useProjectsUrlState({ closedAllowed })
  // Below a sideways tablet the rows are cards, which need no surface of their own.
  const wideScreen = Grid.useBreakpoint().lg !== false

  // Which `view` the shared filters slice should be carrying for this mount,
  // and whether it already is. Active and Archive are two mounts of this one
  // component, so entering either has to re-point the slice before any
  // request goes out.
  const targetView = archived ? PROJECT_ARCHIVE_VIEW : null
  const viewAligned = (filters?.view ?? null) === targetView
  // A link's filters are applied once the slice is re-pointed: the alignment
  // below clears Status and Due date, and would wipe the link's otherwise.
  useFilterUrlSync(
    filters,
    (patch) => updateFilters({ ...patch, page: 1 }),
    redesign && viewAligned
  )

  const handleGetData = useCallback(() => {
    const serializeFilters = serializeData(filters) as ProjectFilters
    const data: ProjectFilters = { ordering, ...serializeFilters }
    if (redesign) {
      data.layout = "command"
      if (quickView !== "all") data.quick_view = quickView
      if (scope === "mine") data.scope = scope
    } else {
      delete data.project_type
      delete data.schedule_health
    }
    // An explicit Completed or Cancelled makes the API add the archived rows
    // back, so a stale one would fill the active list with the Archive.
    if (!archived && data.project_status) {
      const kept = withoutArchivingStatuses(data.project_status)
      if (kept) data.project_status = kept
      else delete data.project_status
    }
    // Projects only ever renders inside a single-branch workspace — the
    // server still independently re-validates this against the caller's
    // real accessible teams (`apps.user.branch_scope`).
    if (activeBranchId) data.team = activeBranchId
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    fetchProjects({
      type: ProjectFetchType.LIST,
      filters: { ...data, page: 1 }
    })
  }, [
    fetchProjects,
    filters,
    ordering,
    archived,
    activeBranchId,
    redesign,
    quickView,
    scope
  ])

  // `setOrdering(null)` clears the stored preference — `useSurfacePreference`
  // then reads back this mount's own default (Last Activity for Active, Date
  // Created for Archive) on the next render, same as never having chosen one.
  const handleOrderingChange = useCallback(
    (next: string | null) => {
      setOrdering(next)
    },
    [setOrdering]
  )

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl(data)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    fetchProjects({
      type: ProjectFetchType.LIST,
      isMore: true,
      filters: extractParamsFromNextUrl<ProjectFilters>(url)
    })
  }, [data, fetchProjects])

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectFilters>) => {
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  // Every filter the panel can set, back to unset. Search, sort and the
  // Active/Archive view stay: the empty state offers to undo the filters only.
  const handleClearFilters = useCallback(() => {
    updateFilters({
      ...CLEARED_FILTERS,
      page: 1,
      project_status: null,
      project_manager: null,
      customer_name: null,
      customer: null,
      end_date_after: null,
      end_date_before: null,
      created_window: null,
      created_after: null,
      created_before: null,
      archive_reason: null,
      archived_at_after: null,
      archived_at_before: null
    })
    // The quick view and My projects narrow the rows as much as a filter does.
    if (redesign) resetViews()
  }, [updateFilters, redesign, resetViews])

  const handleRestore = useCallback(
    async (id: string) => {
      await restoreProject(id)
      handleGetData()
    },
    [restoreProject, handleGetData]
  )

  // An archived project closed while still owed money cannot be deleted; the
  // user is told why in a dialog rather than offered a delete that refuses.
  const [undeletable, setUndeletable] = useState<number | null>(null)

  const handleDelete = useCallback(
    (record: ProjectListItem) => {
      if ((record.unpaid_invoice_count ?? 0) > 0) {
        setUndeletable(record.unpaid_invoice_count as number)
        return
      }
      confirmProjectDelete({
        modal,
        t,
        record,
        deleteProject,
        onDeleted: handleGetData
      })
    },
    [modal, t, deleteProject, handleGetData]
  )

  const handleStatusChange = (
    id: string,
    project_status: number,
    options?: StatusChangeOptions
  ) =>
    sendStatusChange(async (acknowledgement) => {
      await updateProjectStatus({
        project: { id, project_status, ...acknowledgement },
        mode: "list"
      }).unwrap()
      handleGetData()
    }, options)

  // Projects have no bulk endpoint: each action fans the existing per-row call
  // out and comes back with a per-id outcome. The page owns the list refresh
  // and drops the selection only once nothing in it was refused.
  const handleBulkAction = useCallback(
    async ({
      action,
      ids,
      status,
      projectManagerId,
      acknowledgeUnpaidInvoicesFor
    }: ProjectBulkActionParams): Promise<ProjectBulkResult> => {
      const result =
        action === "archive"
          ? await bulkArchiveProjects({ ids }).unwrap()
          : action === "assign-pm"
            ? await bulkPatchProjects({
                ids,
                data: {
                  project_managers: [
                    { id: projectManagerId as string, is_main: true }
                  ]
                }
              }).unwrap()
            : await bulkPatchProjects({
                ids,
                data: { project_status: status as number },
                ...(acknowledgeUnpaidInvoicesFor?.length
                  ? { acknowledgeUnpaidInvoicesFor }
                  : {})
              }).unwrap()

      handleGetData()
      if (!result.failed.length) setSelectedIds([])
      return result
    },
    [bulkArchiveProjects, bulkPatchProjects, handleGetData]
  )

  // Any real filter change is a different working set of rows, so it clears
  // whatever was ticked. Paging and sorting are excluded by the shared token.
  const selectionToken = selectionResetToken(filters)
  useEffect(() => {
    setSelectedIds([])
  }, [selectionToken, quickView, scope])

  const handleUpdateProject = useCallback(
    async (project: ProjectEditableRowData) => {
      await updateProject(project)
      handleGetData()
    },
    [handleGetData, updateProject]
  )

  const handleImportLeads = useCallback(
    async (file: File) => {
      try {
        await importProjects(file).unwrap()
        handleGetData()
      } catch (e) {
        console.log(e)
      }
    },
    [importProjects, handleGetData]
  )

  const handleGetImportLeadsTemplates = useCallback(async () => {
    const blob = await getImportProjectsTemplates()?.unwrap()
    downloadBlob(blob, `import-projects-template.xlsx`)
  }, [getImportProjectsTemplates])

  const handleSelectRow = useCallback(
    (record: ProjectListItem, action: string) => {
      switch (action) {
        case "update":
          return handleUpdateProject(record)
        case "edit":
        case "manage":
        case "view": {
          const route = projectRowActionRoute(action, record.id)
          if (route) navigate(route)
          break
        }
        case "archive":
          return setProjectToArchive(record)
        case "restore":
          return handleRestore(record.id)
        case "delete":
          return handleDelete(record)
      }
    },
    [handleUpdateProject, handleRestore, handleDelete, navigate]
  )

  useEffect(() => {
    // Active and Archive are now separate mounts of this same component
    // (rather than one Segmented switching a shared mount), so this effect is
    // the only place left to align the shared filters slice on entry. Both
    // tabs' narrowing filters are cleared, not just the one being left: the
    // slice merges, and neither tab renders a control for the other's
    // filters — so a survivor is a permanently empty list with no way out.
    if (!viewAligned) {
      updateFilters({
        view: targetView,
        // The Archive embeds this page without its header, so it has no
        // search box: a term typed on the Active list would filter it unseen.
        q: null,
        archive_reason: null,
        archived_at_after: null,
        archived_at_before: null,
        project_status: null,
        end_date_after: null,
        end_date_before: null,
        page: 1
      })
    }
  }, [viewAligned, targetView, updateFilters])

  useEffect(() => {
    // Both this and the alignment effect above run in the same commit, so
    // firing unconditionally would send one request built from the *previous*
    // mount's filters — the active list's rows, rendered under the archive's
    // columns, before the real archive request replaced them. Wait for the
    // slice to agree with this mount before asking the server anything.
    if (!viewAligned) return
    handleGetData()
  }, [handleGetData, viewAligned])

  useEffect(() => {
    if (!embedded) generateDocumentTitle("Projects")
    fetchFilterOptions()
  }, [fetchFilterOptions, embedded])

  const projectTable = (
    <ProjectListTable
      loading={fetching}
      projects={data?.results || []}
      ordering={ordering}
      total={data?.count || 0}
      filters={filters}
      projectPermissions={projectPermissions}
      filterOptions={filterOptions}
      archived={archived}
      selectedIds={selectedIds}
      onSelectedIdsChange={setSelectedIds}
      onBulkAction={handleBulkAction}
      onStatusChange={handleStatusChange}
      onSelectRow={handleSelectRow}
      onOrderingChange={handleOrderingChange}
      next={data?.next}
      retryNext={data?.retryNext}
      loadFailed={data?.loadFailed}
      onRetry={handleGetData}
      onClearFilters={handleClearFilters}
      onLoadMore={handleLoadMore}
      redesign={redesign}
      scheduleShown={scheduleShown}
      narrowingViews={
        redesign ? Number(quickView !== "all") + Number(scope === "mine") : 0
      }
      tableSettingsControl={{
        open: tableSettingsOpen,
        onOpenChange: setTableSettingsOpen
      }}
    />
  )

  return (
    <div className={"grid"}>
      <Flex gap={"middle"} vertical className={"overflow-auto"}>
        {redesign ? (
          <Suspense
            fallback={<Skeleton active title paragraph={{ rows: 1 }} />}
          >
            <ProjectsCommandHeader
              projectPermissions={projectPermissions}
              counts={quickViewCounts}
              search={filters?.q || ""}
              onFiltersChange={handleChangeFilter}
              onRefetchProjects={handleGetData}
              onImport={handleImportLeads}
              onDownloadTemplate={handleGetImportLeadsTemplates}
              onOpenTableSettings={
                wideScreen ? () => setTableSettingsOpen(true) : undefined
              }
            />
          </Suspense>
        ) : null}
        {!embedded && !redesign && (
          <ProjectListTableHeader
            projectPermissions={projectPermissions}
            search={filters?.q || ""}
            onImport={handleImportLeads}
            onFilersChange={handleChangeFilter}
            onRefetchProjectsCallback={handleGetData}
            onDownloadTemplate={handleGetImportLeadsTemplates}
          />
        )}

        <div data-guide-target="projects-list-panel" className="contents">
          {redesign ? (
            <Flex vertical>
              <div className="border-0 border-b border-solid border-slate-200">
                <ProjectQuickViews
                  value={quickView}
                  counts={quickViewCounts}
                  closedAllowed={closedAllowed}
                  onChange={setQuickView}
                />
              </div>
              <ProjectCommandToolbar
                filters={filters}
                filterOptions={filterOptions}
                scope={scope}
                onScopeChange={setScope}
                onFiltersChange={handleChangeFilter}
              />
              {/* The table is its own surface: white, a light border, the
                  theme radius and no heavy shadow. */}
              <div
                className={
                  wideScreen
                    ? "mt-4 overflow-hidden rounded-lg border border-solid border-slate-200 bg-white"
                    : "mt-2"
                }
              >
                {projectTable}
              </div>
            </Flex>
          ) : (
            <CardComponent classNames={{ body: "p-0" }}>
              <ProjectListTableFilters
                filters={filters}
                filterOptions={filterOptions}
                archived={archived}
                onFilersChange={handleChangeFilter}
              />
              {projectTable}
            </CardComponent>
          )}
        </div>
      </Flex>

      {projectToArchive && (
        <ArchiveProjectModal
          open
          projectId={projectToArchive.id}
          projectName={projectToArchive.lead_name}
          projectStatus={projectToArchive.project_status}
          canClose={canCloseProject(projectPermissions, projectToArchive)}
          onCancel={() => setProjectToArchive(null)}
          onArchived={() => {
            setProjectToArchive(null)
            handleGetData()
          }}
        />
      )}

      <UnpaidInvoicesDialog
        open={undeletable != null}
        mode="delete"
        count={undeletable ?? 0}
        onClose={() => setUndeletable(null)}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.projects.listData,
  filters: state.projects.listFilters,
  fetching: state.projects.listFetching || state.projects.fetchingFilterOptions,
  filterOptions: state.projects.filterOptions,
  projectPermissions: state.auth.permissions?.codes?.projects,
  quickViewCounts: state.projects.listQuickViewCounts,
  redesignEnabled: !!state.auth.userInfo?.feature_projects_redesign,
  // Next milestone folds into Schedule only while Table settings shows it.
  scheduleShown: scheduleColumnShown(
    selectTableTabColumns(state, PROJECT_COMMAND_SURFACE, DEFAULT_TAB)
  )
})

const mapDispatchToProps = {
  fetchProjects,
  updateProjectStatus,
  updateProject: updateListProject,
  fetchFilterOptions: fetchProjectsFilterOptions,
  updateFilters: projectsSetListFilters,
  importProjects,
  restoreProject,
  deleteProject,
  bulkPatchProjects,
  bulkArchiveProjects,
  getImportProjectsTemplates: fetchImportProjectsTemplates
}

const connector = connect(mapStateToProps, mapDispatchToProps)

// Active and Archive used to be one Segmented-driven view; they're now two
// separate mounts of the exact same base component, so every filter, column,
// action and style stays byte-for-byte identical between the two.
function ProjectsListPage(props: ConnectedProps<typeof connector>) {
  return <ProjectsListPageBase {...props} archived={false} />
}

export function ProjectsArchivePage(
  props: ConnectedProps<typeof connector> & { embedded?: boolean }
) {
  return <ProjectsListPageBase {...props} archived />
}

export default connector(ProjectsListPage)
export const ConnectedProjectsArchivePage = connector(ProjectsArchivePage)
